import React, {ReactNode, useEffect, useRef, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  selectIsSubmitting,
  selectRemainingQuestions,
  selectSurveyAnswers,
  setBodyType
} from "../../redux/survey/macroSurveySlice";
import {BodyType, Gender} from "../../models/fitnessProfile";
import {HeroHeading2} from "../text";
import {Button} from "../buttons/Button";
import {Controller, Grid, Navigation, Pagination, Scrollbar, Swiper as SwiperClass} from "swiper";
import {Swiper, SwiperSlide} from "swiper/react";
import {NavigationOptions} from "swiper/types";
import {ArrowBackExtendedIcon, ArrowNextExtendedIcon} from "../icons";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";

export const MacroSurveyBodyTypeQuestionView: React.FC<any> = ({ }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const answers = useAppSelector(selectSurveyAnswers)
  const remainingQuestions = useAppSelector(selectRemainingQuestions)
  const isSubmitting = useAppSelector(selectIsSubmitting)


  const options: BodyType[] = [
    BodyType.lean,
    BodyType.moderatelyLean,
    BodyType.average,
    BodyType.moderatelyOverweight,
    BodyType.overweight,
    BodyType.obese
  ]

  const nextRef = useRef(null)
  const prevRef = useRef(null)

  const [controlledSwiper, setControlledSwiper] = useState<SwiperClass>();
  const [swiperIndex, setSwiperIndex] = useState<number>(options.indexOf(answers.bodyType));

  const [bodyTypeAnswer, setBodyTypeAnswer] = useState<BodyType>(answers.bodyType)
  const [isValid, setIsValid] = useState<boolean>(false)

  const getImage = (bodyType: BodyType): ReactNode => {
    switch (bodyType) {
      case BodyType.lean:
        return <img className="h-full w-full object-contain" src={`../../images/survey_graphic_${answers.gender === Gender.male ? 'male' : 'female'}_body_type_1.webp`} />
      case BodyType.moderatelyLean:
        return <img className="h-full w-full object-contain" src={`../../images/survey_graphic_${answers.gender === Gender.male ? 'male' : 'female'}_body_type_2.webp`} />
      case BodyType.average:
        return <img className="h-full w-full object-contain" src={`../../images/survey_graphic_${answers.gender === Gender.male ? 'male' : 'female'}_body_type_3.webp`} />
      case BodyType.moderatelyOverweight:
        return <img className="h-full w-full object-contain" src={`../../images/survey_graphic_${answers.gender === Gender.male ? 'male' : 'female'}_body_type_4.webp`} />
      case BodyType.overweight:
        return <img className="h-full w-full object-contain" src={`../../images/survey_graphic_${answers.gender === Gender.male ? 'male' : 'female'}_body_type_5.webp`} />
      case BodyType.obese:
        return <img className="h-full w-full object-contain" src={`../../images/survey_graphic_${answers.gender === Gender.male ? 'male' : 'female'}_body_type_6.webp`} />
    }
  }

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Nutrition Survey' }, { 'Survey Question': 'Body Type' }))
  }, [])

  useEffect(() => {
    if (bodyTypeAnswer) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }
  }, [bodyTypeAnswer])

  return <div className="flex flex-col space-y-8 lg:space-y-10 items-center w-full">
    <HeroHeading2 className="text-center mx-8">{t('macro_survey:macro_survey_header_title_body_type')}</HeroHeading2>

    <div className="flex space-x-2 px-4 w-full justify-items-center">
      <div
        onClick={(e) => {
          e.stopPropagation()
          const currentIndex = controlledSwiper?.activeIndex ?? 0
          const slidesPerView = controlledSwiper?.params.slidesPerView as number ?? 0
          let nextIndex = currentIndex - slidesPerView
          nextIndex = nextIndex < 0 ? options.length - 1 : nextIndex > options.length - 1 ? 0 : nextIndex
          controlledSwiper?.slideTo(nextIndex)
        }}
        className={`cursor-pointer flex-shrink-0 bg-fg-primary-lightest flex items-center justify-center w-10 h-10 lg:w-14 lg:h-14 p-2 lg:p-3.5 m-auto rounded-full`}
        ref={prevRef}>
        <ArrowBackExtendedIcon className="h-4 w-4 lg:h-6 lg:w-6 text-fg-primary" />
      </div>

      <Swiper
        onInit={(swiper) => {
          (swiper.params.navigation as NavigationOptions).prevEl = prevRef.current;
          (swiper.params.navigation as NavigationOptions).nextEl = nextRef.current;
          swiper.navigation.init();
          swiper.navigation.update();

          swiper.slideTo(swiperIndex)
        }}
        className="flex-grow h-full w-full"
        modules={[Navigation, Grid, Pagination, Scrollbar, Controller]}
        onSwiper={swiper => {
          setControlledSwiper(swiper)
        }}
        slidesPerView={1}
        pagination={{ clickable: true }}
        scrollbar={{ draggable: true }}
        onSlideChange={(swiper) => {
          setSwiperIndex(swiper.activeIndex)
          setBodyTypeAnswer(options[swiper.activeIndex])
        }}
      >
        {options.map(option => {
          return <SwiperSlide key={option.toString()}>
            {getImage(option)}
          </SwiperSlide>
        })}
      </Swiper>

      <div
        onClick={(e) => {
          e.stopPropagation()
          const currentIndex = controlledSwiper?.activeIndex ?? 0
          const slidesPerView = controlledSwiper?.params.slidesPerView as number ?? 0
          let nextIndex = currentIndex + slidesPerView
          nextIndex = nextIndex < 0 ? options.length - 1 : nextIndex > options.length - 1 ? 0 : nextIndex
          controlledSwiper?.slideTo(nextIndex)
        }}
        className={`cursor-pointer flex-shrink-0 bg-fg-primary-lightest flex items-center justify-center w-10 h-10 lg:w-14 lg:h-14 p-2 lg:p-3.5 m-auto rounded-full`}
        ref={nextRef}>
        <ArrowNextExtendedIcon className="h-4 w-4 lg:h-6 lg:w-6 text-fg-primary" />
      </div>
    </div>

    <div className="flex space-x-2">
      {options.map((option, index) => {
        return <div key={option.toString()} className={`rounded-full h-2.5 w-2.5 ${index === swiperIndex ? 'bg-fg-primary' : 'bg-fg-gray-surface'}`} />
      })}
    </div>

    <div className="w-full px-5 md:justify-center md:flex">
      <Button
        isDisabled={!isValid}
        isLoading={isSubmitting}
        size="lg"
        isFullWidth={true}
        onClick={() => {
          bodyTypeAnswer && dispatch(setBodyType(bodyTypeAnswer))
        }}
      >
        {remainingQuestions.length ? t('common:common_title_continue') : t('common:common_title_submit')}
      </Button>
    </div>
  </div>
}